import React, { useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import styles from './latestActivitiesSection.module.scss';
import { addAndRemoveLoadingIcon } from './driver_page_components/payoutAdjustmentsSection';
import { API_ROOT, sendRequest } from '../../../supplementary_components/requests';
import { CustomLoadingIcon } from '../../../supplementary_components/loading_icon';

/** Strip dollar amounts from load rate / driver-payout activity lines. */
export const redactRateSensitiveActivityDesc = (desc, canSeeRates, hideDriverPayoutAmount = false) => {
    if (desc == null) return desc;
    const text = String(desc).trim();
    if (!text) return desc;

    // Updaters who cannot see driver payout: never show amount/truck detail
    // (covers new "Driver Payout …" and legacy "Pay To Driver …" lines).
    if (hideDriverPayoutAmount) {
        if (/^Driver Payout\b/i.test(text)) return "Driver Payout was changed";
        if (/^Pay To Driver\b/i.test(text)) return "Pay To Driver was changed";
    }

    if (canSeeRates) return desc;
    if (/^Payout was changed from\b/i.test(text)) return "Payout was changed";
    if (/^Pay To Driver\b/i.test(text)) return "Pay To Driver was changed";
    if (/^Driver Payout\b/i.test(text)) return "Driver Payout was changed";
    if (/^(Payout|Pay To Driver|Driver Payout)\b/i.test(text) && /\$/.test(text)) {
        return text.replace(/\s+from\s+.+$/i, "").trim() || text;
    }
    return desc;
};

export const LatestActivitiesSection = ({type, initData, objectId, refreshKey, emptyMessage, canSeeRates = true, hideDriverPayoutAmount = false}) => {
    const isFirst = useRef(true);
    // If initData has 5 items, show only 4 initially
    const initialList = initData ? (initData.length === 5 ? initData.slice(0, 4) : initData) : [];
    const [sectionData, setSectionData] = useState({
        activityList : initialList,
        checkForMoreButton : initData ? (type === "LOAD" && initData.length === 3 ? true :initData.length === 5 ? true : false) : null
    })
    const [isLoading, setIsLoading] = useState(!initData && objectId && type);
    
    // Fetch initial data if initData is not provided (for trucks)
    useEffect(() => {
        if (!initData && objectId && type) {
            setIsLoading(true);
            sendRequest("GET", API_ROOT+"activities/previousActivities?type="+type+"&targetId="+objectId, null, (res)=>{
                setIsLoading(false);
                if(res && res.length > 0){
                    // If 5 items returned, show only 4 and enable "Load More" button
                    const itemsToShow = res.length === 5 ? res.slice(0, 4) : res;
                    setSectionData({
                        activityList: itemsToShow,
                        checkForMoreButton: res.length === 5 ? true : false
                    });
                } else {
                    setSectionData({
                        activityList: [],
                        checkForMoreButton: false
                    });
                }
            }, ()=>{
                setIsLoading(false);
                setSectionData({
                    activityList: [],
                    checkForMoreButton: false
                });
            });
        }
    }, [initData, objectId, type]);
    
    useEffect(() => {
        if (isFirst.current) {       // skip the very first run
            isFirst.current = false;
            return;
        }
        if (initData) {
            // If 5 items returned, show only 4 and enable "Load More" button
            const itemsToShow = initData.length === 5 ? initData.slice(0, 4) : initData;
            setSectionData(prevData =>  {
                return {
                    checkForMoreButton: type === "LOAD" && initData.length === 3 ? true : initData.length === 5 ? true : false,
                    activityList : itemsToShow
                }
            })
        }
        return () => {
        }
        // eslint-disable-next-line
    }, [initData, refreshKey])
    // const [activityList, setActivityList] = useState(initData);
    // const [loadActivitiesButton, setLoadActivitiesButton] = useState(activityList.length > 2);//
    const targetTimezone = localStorage.getItem("defaultTimezone") || "America/Chicago";
    
    // Show loading icon when fetching initial data
    if (isLoading) {
        return <div className={styles.wrapper}>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <CustomLoadingIcon height={'2rem'} speed={1.0} />
            </div>
        </div>
    }

    if (sectionData.activityList.length === 0 && emptyMessage) {
        return <div className={styles.wrapper}>
            <div className={styles.emptyMessage}>{emptyMessage}</div>
        </div>
    }
    
    return <div className={styles.wrapper}>
        {
            sectionData.activityList.map((val, index)=> {
                const dtUtc = DateTime.fromISO(val.timestamp);
                const dtInZone = dtUtc.setZone(targetTimezone);
                const formattedTime = dtInZone.toFormat('MM/dd hh:mm a');
                const descArray = Array.isArray(val.description) ? val.description : val.description?.slice(0, -1).split(";") || []
                return <div key={index} className={styles.singleActivity}>
                    <span className={styles.author}>{val.employeeName}</span>
                    <span className={styles.timestamp}>{formattedTime}</span>
                    <div className={styles.activityList}>
                    {
                        descArray.map((desc, ind)=>{
                            const safeDesc = redactRateSensitiveActivityDesc(desc, canSeeRates, hideDriverPayoutAmount);
                            return <div key={ind}>{"- " + safeDesc +"."}</div>;
                        })
                    }
                    </div>
                </div>
            })
        }
        { sectionData.checkForMoreButton == null ? 
        <div className={`${styles.buttonContainer} non-selectable`}>
            <div className={styles.noMoreToload}>No more activities</div>
        </div>
        : sectionData.checkForMoreButton ?
        <div className={`${styles.buttonContainer} non-selectable`}>
            <div onClick={(e)=>{
                if(!e.target.className.includes(`${styles.loading}`)){
                    const target = e.target.childNodes[0];
                    e.target.className = styles.loading;
                    addAndRemoveLoadingIcon(target, false);
                    sendRequest("GET", API_ROOT+"activities/loadPreviousActivities?lastActivityTimestamp="+sectionData.activityList[sectionData.activityList.length-1].timestamp+"&type="+type+"&targetId="+objectId, null, (res)=>{
                        addAndRemoveLoadingIcon(target, true);
                        e.target.className = "";
                        if(res === null || res.length === 0){
                            setSectionData(prevData =>  {
                                return {
                                    ...prevData,
                                    checkForMoreButton : null
                                }
                            })
                        }else{
                            // If 5 items returned, show only 4 and enable "Load More" button
                            const itemsToAdd = res.length === 5 ? res.slice(0, 4) : res;
                            setSectionData(prevData => {
                                return {
                                    activityList : [...prevData.activityList, ...itemsToAdd ],
                                    checkForMoreButton : res.length === 5 ? true : null
                                }
                            });
                        }
                    })

                }
                
            }}>Load more </div>
        </div>
        : null
        }
        
    </div>
}